import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Card, CardContent, Typography, Grid, Chip, TextField,
  Select, MenuItem, FormControl, InputLabel, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Button,
  LinearProgress, TablePagination, InputAdornment, Skeleton,
} from "@mui/material";
import {
  HowToReg, People, Person, MyLocation, Search, FileDownload,
  TrendingUp, SignalCellularAlt,
} from "@mui/icons-material";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

// ─── Types ────────────────────────────────────────────────────────────────────

interface WaitlistEntry {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  zipCode: string;
  role: "customer" | "technician";
  createdAt: any;
  market?: string;
}

// ─── Utils ────────────────────────────────────────────────────────────────────

function formatDate(ts: any): string {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function toDateMs(ts: any): number {
  if (!ts) return 0;
  try {
    if (ts.toDate) return ts.toDate().getTime();
    if (ts.seconds) return ts.seconds * 1000;
    return new Date(ts).getTime();
  } catch {
    return 0;
  }
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

function StatCard({ label, value, sub, icon, color, loading }: StatCardProps) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
              {label}
            </Typography>
            {loading ? (
              <Skeleton width={80} height={40} />
            ) : (
              <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
                {value}
              </Typography>
            )}
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{ bgcolor: `${color}22`, borderRadius: 2, p: 1.5, color }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

export function WaitlistView() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "customer" | "technician">("all");
  const [page, setPage] = useState(0);
  const ROWS_PER_PAGE = 25;

  // Real-time Firestore listener
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "waitlist"), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() } as WaitlistEntry));
      // Sort newest first
      data.sort((a, b) => toDateMs(b.createdAt) - toDateMs(a.createdAt));
      setEntries(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // ─── Stats ──────────────────────────────────────────────────────────────────

  const totalSignups = entries.length;
  const customerCount = entries.filter((e) => e.role === "customer").length;
  const technicianCount = entries.filter((e) => e.role === "technician").length;
  const uniqueZips = new Set(entries.map((e) => e.zipCode?.trim()).filter(Boolean)).size;

  // ─── Zip intelligence ───────────────────────────────────────────────────────

  const zipCounts = useMemo(() => {
    const map: Record<string, number> = {};
    entries.forEach((e) => {
      const z = e.zipCode?.trim();
      if (z) map[z] = (map[z] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [entries]);

  const maxZipCount = zipCounts[0]?.[1] || 1;

  // ─── Filtered table data ─────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return entries.filter((e) => {
      const matchRole = roleFilter === "all" || e.role === roleFilter;
      const matchSearch =
        !q ||
        `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.zipCode?.toLowerCase().includes(q);
      return matchRole && matchSearch;
    });
  }, [entries, search, roleFilter]);

  // Reset page when filter changes
  useEffect(() => { setPage(0); }, [search, roleFilter]);

  const paged = filtered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  // ─── CSV Export ──────────────────────────────────────────────────────────────

  const handleExport = () => {
    const headers = ["First Name", "Last Name", "Email", "Zip Code", "Role", "Signed Up", "Market"];
    const rows = entries.map((e) => [
      e.firstName || "",
      e.lastName || "",
      e.email || "",
      e.zipCode || "",
      e.role || "",
      formatDate(e.createdAt),
      e.market || "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Empty state ─────────────────────────────────────────────────────────────

  if (!loading && entries.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 10, color: "text.secondary" }}>
        <HowToReg sx={{ fontSize: 64, color: "#663399", mb: 2, opacity: 0.5 }} />
        <Typography variant="h5" fontWeight={700} mb={1}>
          No signups yet
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Share the waitlist link to get started!
        </Typography>
        <Typography
          component="a"
          href="https://sagetechapp.com/#waitlist"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "#663399", fontWeight: 700, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
        >
          sagetechapp.com/#waitlist ↗
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Stat Cards ── */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Total Signups"
            value={loading ? "—" : totalSignups}
            sub="all time"
            icon={<HowToReg />}
            color="#663399"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Customers"
            value={loading ? "—" : customerCount}
            sub={loading ? "" : `${totalSignups ? Math.round((customerCount / totalSignups) * 100) : 0}% of signups`}
            icon={<Person />}
            color="#3b82f6"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Technicians"
            value={loading ? "—" : technicianCount}
            sub={loading ? "" : `${totalSignups ? Math.round((technicianCount / totalSignups) * 100) : 0}% of signups`}
            icon={<People />}
            color="#FF8C00"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Markets"
            value={loading ? "—" : uniqueZips}
            sub="unique zip codes"
            icon={<MyLocation />}
            color="#22c55e"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* ── Expansion Intelligence ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <TrendingUp sx={{ color: "#663399" }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Expansion Intelligence — Where to Launch Next
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Top zip codes by signup demand · use this to decide your next market
              </Typography>
            </Box>
          </Box>

          {loading ? (
            [1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={44} sx={{ mb: 1 }} />)
          ) : zipCounts.length === 0 ? (
            <Typography color="text.secondary" variant="body2">No zip data yet.</Typography>
          ) : (
            zipCounts.map(([zip, count], idx) => {
              const pct = totalSignups ? Math.round((count / totalSignups) * 100) : 0;
              const barPct = Math.round((count / maxZipCount) * 100);
              return (
                <Box
                  key={zip}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 1.25,
                    borderBottom: idx < zipCounts.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                  }}
                >
                  {/* Rank */}
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ width: 24, color: idx === 0 ? "#FF8C00" : "text.secondary", textAlign: "right" }}
                  >
                    #{idx + 1}
                  </Typography>

                  {/* Zip */}
                  <Typography variant="body1" fontWeight={700} sx={{ width: 70, fontFamily: "monospace" }}>
                    {zip}
                  </Typography>

                  {/* Bar */}
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={barPct}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "rgba(255,255,255,0.07)",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: idx === 0 ? "#FF8C00" : "#663399",
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>

                  {/* Count */}
                  <Typography variant="body2" fontWeight={700} sx={{ width: 36, textAlign: "right" }}>
                    {count}
                  </Typography>

                  {/* Pct */}
                  <Chip
                    label={`${pct}%`}
                    size="small"
                    sx={{
                      bgcolor: "rgba(102,51,153,0.15)",
                      color: "#663399",
                      fontWeight: 700,
                      width: 52,
                      fontSize: "0.7rem",
                    }}
                  />
                </Box>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* ── Signups Table ── */}
      <Card>
        <CardContent>
          {/* Table header / filters */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SignalCellularAlt sx={{ color: "#663399" }} />
              <Typography variant="h6" fontWeight={700}>
                Signups
              </Typography>
              {!loading && (
                <Chip
                  label={filtered.length}
                  size="small"
                  sx={{ bgcolor: "rgba(102,51,153,0.15)", color: "#663399", fontWeight: 700 }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search name, email, zip…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 240 }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => setRoleFilter(e.target.value as any)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="customer">Customers</MenuItem>
                  <MenuItem value="technician">Technicians</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownload />}
                onClick={handleExport}
                sx={{ borderColor: "rgba(255,255,255,0.15)", color: "text.secondary", "&:hover": { borderColor: "#663399", color: "#663399" } }}
              >
                Export CSV
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "transparent" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 0.5, borderBottom: "1px solid rgba(255,255,255,0.08)" } }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Zip Code</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Signed Up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      {[1, 2, 3, 4, 5].map((j) => (
                        <TableCell key={j}><Skeleton height={24} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                      No signups match your filter.
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((entry) => (
                    <TableRow
                      key={entry.id}
                      sx={{
                        "&:hover": { bgcolor: "rgba(102,51,153,0.06)" },
                        "& td": { borderBottom: "1px solid rgba(255,255,255,0.04)", py: 1.25 },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {entry.firstName} {entry.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {entry.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                          {entry.zipCode || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.role === "technician" ? "Technician" : "Customer"}
                          size="small"
                          sx={{
                            bgcolor: entry.role === "technician" ? "rgba(255,140,0,0.15)" : "rgba(102,51,153,0.18)",
                            color: entry.role === "technician" ? "#FF8C00" : "#a78bfa",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(entry.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            sx={{ borderTop: "1px solid rgba(255,255,255,0.06)", mt: 1, color: "text.secondary" }}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
